import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

export default function CoachSecurity() {
  const [loading, setLoading] = useState(true);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setTotpEnabled(Boolean(data.totp_enabled));
    } catch {
      toast.error("Impossible de charger le statut 2FA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup-totp");
      setSetupData(data);
      setVerifyCode("");
      toast.success("Scannez le QR code avec votre application d'authentification");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de la configuration");
    } finally {
      setBusy(false);
    }
  };

  const handleEnable = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error("Entrez un code à 6 chiffres");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable-totp", { code: verifyCode });
      setTotpEnabled(true);
      setSetupData(null);
      setVerifyCode("");
      toast.success("Double authentification activée");
    } catch (err) {
      toast.error(err.response?.data?.error || "Code invalide");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error("Entrez un code à 6 chiffres");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      setTotpEnabled(false);
      setSetupData(null);
      setDisableCode("");
      toast.success("Double authentification désactivée");
    } catch (err) {
      toast.error(err.response?.data?.error || "Code invalide");
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!setupData?.secret) return;
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Secret copié");
    } catch {
      toast.error("Impossible de copier le secret");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-4 md:p-0">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="w-7 h-7 text-blue-600" />
          Sécurité du compte
        </h1>
        <p className="text-slate-500 mt-1">
          Protégez votre compte coach avec une authentification à deux facteurs (TOTP).
        </p>
      </div>

      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${totpEnabled ? "bg-green-100" : "bg-slate-100"}`}>
            {totpEnabled ? (
              <ShieldCheck className="w-6 h-6 text-green-600" />
            ) : (
              <ShieldOff className="w-6 h-6 text-slate-500" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-lg text-slate-900">
              Authentification TOTP
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {totpEnabled
                ? "La double authentification est active sur votre compte."
                : "Recommandé : activez Google Authenticator, Authy ou une app compatible."}
            </p>
            <span
              className={`inline-block mt-3 text-xs font-medium px-2.5 py-1 rounded-full ${
                totpEnabled
                  ? "bg-green-100 text-green-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {totpEnabled ? "Activé" : "Désactivé"}
            </span>
          </div>
        </div>
      </Card>

      {!totpEnabled && !setupData && (
        <Card className="p-6">
          <h3 className="font-semibold text-slate-900 mb-2">Configurer le TOTP</h3>
          <p className="text-sm text-slate-500 mb-4">
            Générez un QR code à scanner avec votre application d'authentification.
          </p>
          <Button onClick={handleSetup} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
            Commencer la configuration
          </Button>
        </Card>
      )}

      {!totpEnabled && setupData && (
        <Card className="p-6 space-y-5">
          <h3 className="font-semibold text-slate-900">Étape 1 — Scanner le QR code</h3>
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className="bg-white p-3 rounded-lg border border-slate-200 shrink-0">
              <QRCodeSVG
                value={setupData.provisioning_uri || setupData.qr_url}
                size={180}
                level="M"
              />
            </div>
            <div className="space-y-3 flex-1">
              <p className="text-sm text-slate-500">
                Ou entrez ce secret manuellement dans votre application :
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg text-sm font-mono break-all">
                  {setupData.secret}
                </code>
                <Button variant="outline" size="icon" onClick={copySecret}>
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="border-t pt-5">
            <h3 className="font-semibold text-slate-900 mb-2">Étape 2 — Vérifier le code</h3>
            <p className="text-sm text-slate-500 mb-3">
              Entrez le code à 6 chiffres affiché par votre application.
            </p>
            <div className="flex gap-3">
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                className="max-w-[160px] font-mono text-lg tracking-widest"
              />
              <Button onClick={handleEnable} disabled={busy || verifyCode.length !== 6}>
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Activer
              </Button>
            </div>
          </div>
        </Card>
      )}

      {totpEnabled && (
        <Card className="p-6 border-red-100">
          <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-red-500" />
            Désactiver la 2FA
          </h3>
          <p className="text-sm text-slate-500 mb-3">
            Entrez un code TOTP valide pour confirmer la désactivation.
          </p>
          <div className="flex gap-3">
            <Input
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              className="max-w-[160px] font-mono text-lg tracking-widest"
            />
            <Button variant="destructive" onClick={handleDisable} disabled={busy || disableCode.length !== 6}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Désactiver
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}